import { useState } from 'react';
import { DollarSign, ChevronDown, ChevronUp, Zap, History, TrendingUp } from 'lucide-react';
import type { NEMDetailedPrices, NEMPeriod, NEMHistoricalBlock } from '../../api/client';

interface PriceBlocksCardProps {
  prices: NEMDetailedPrices | null;
  loading?: boolean;
  error?: string | null;
}

// Color based on price level ($/kWh)
function getPriceColor(priceKwh: number): string {
  if (priceKwh < 0) return 'bg-green-500'; // Negative = export rebate
  if (priceKwh < 0.10) return 'bg-green-400';
  if (priceKwh < 0.20) return 'bg-emerald-400';
  if (priceKwh < 0.30) return 'bg-yellow-400';
  if (priceKwh < 0.40) return 'bg-orange-400';
  if (priceKwh < 0.50) return 'bg-red-400';
  return 'bg-red-600'; // Very high
}

function getPriceTextColor(priceKwh: number): string {
  if (priceKwh < 0) return 'text-green-600 dark:text-green-400';
  if (priceKwh < 0.15) return 'text-green-600 dark:text-green-400';
  if (priceKwh < 0.25) return 'text-yellow-600 dark:text-yellow-400';
  if (priceKwh < 0.35) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
}

function formatPrice(priceKwh: number): string {
  return `${(priceKwh * 100).toFixed(1)}c`;
}

function formatTime(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
}

// 5-minute period block (shown when expanded)
function PeriodBlock({ period }: { period: NEMPeriod }) {
  const bgColor = period.is_actual ? getPriceColor(period.price_kwh) : 'bg-gray-300 dark:bg-gray-600';
  const highlight = period.is_current ? 'ring-2 ring-offset-1 ring-gray-900 dark:ring-white scale-105' : '';

  return (
    <div className={`flex flex-col items-center p-1.5 rounded-md ${bgColor} ${highlight} min-w-[44px] transition-all`}>
      <span className="text-[10px] text-white/80 font-medium">P{period.period}</span>
      <span className="text-sm text-white font-bold">
        {period.is_actual ? formatPrice(period.price_kwh) : '?'}
      </span>
      {period.is_current && (
        <span className="text-[8px] text-white bg-black/30 px-1.5 py-0.5 rounded mt-1 font-medium">NOW</span>
      )}
    </div>
  );
}

// Historical 30-minute block (settled prices)
function HistoricalBlock({ block }: { block: NEMHistoricalBlock }) {
  const bgColor = getPriceColor(block.price_kwh);

  return (
    <div
      className={`
        flex flex-col items-center p-2 rounded-lg transition-all
        ${bgColor} opacity-60 hover:opacity-80
        min-w-[52px]
      `}
    >
      <span className="text-[10px] text-white/70 font-medium">
        {formatTime(block.start_time)}
      </span>
      <span className="text-sm text-white font-bold drop-shadow-sm">
        {formatPrice(block.price_kwh)}
      </span>
      <span className="text-[8px] text-white/50 mt-0.5">settled</span>
    </div>
  );
}

// 30-minute block (main display)
function ThirtyMinBlock({
  startTime,
  endTime: _endTime,
  priceKwh,
  isCurrent,
  isForecast,
  periods,
  expanded,
  onToggle,
}: {
  startTime: string;
  endTime: string;
  priceKwh: number;
  isCurrent: boolean;
  isForecast: boolean;
  periods?: NEMPeriod[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const bgColor = getPriceColor(priceKwh);
  const canExpand = isCurrent && periods && periods.length > 0;

  return (
    <div className="flex flex-col">
      <button
        onClick={canExpand ? onToggle : undefined}
        className={`
          flex flex-col items-center p-2 rounded-lg transition-all
          ${bgColor} ${isCurrent ? 'shadow-lg scale-110 ring-2 ring-offset-2 ring-offset-gray-100 dark:ring-offset-gray-800 ring-current' : 'opacity-70 hover:opacity-90'}
          ${canExpand ? 'cursor-pointer' : 'cursor-default'}
          ${isForecast ? 'border border-dashed border-white/20' : ''}
          min-w-[52px]
        `}
      >
        <span className="text-[10px] text-white/70 font-medium">
          {formatTime(startTime)}
        </span>
        <span className="text-sm text-white font-bold drop-shadow-sm">
          {formatPrice(priceKwh)}
        </span>
        {isCurrent && (
          <div className="flex items-center gap-1 mt-1">
            <span className="text-[9px] text-white bg-black/30 px-1.5 py-0.5 rounded font-medium">NOW</span>
            {canExpand && (
              expanded ? <ChevronUp className="w-3.5 h-3.5 text-white" /> : <ChevronDown className="w-3.5 h-3.5 text-white" />
            )}
          </div>
        )}
        {isForecast && !isCurrent && (
          <span className="text-[9px] text-white/50 mt-0.5 italic">forecast</span>
        )}
      </button>

      {/* Expanded 5-minute periods */}
      {expanded && periods && (
        <div className="flex gap-1.5 mt-3 p-2 bg-gray-100 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          {periods.map((period) => (
            <PeriodBlock key={period.period} period={period} />
          ))}
        </div>
      )}
    </div>
  );
}

export function PriceBlocksCard({ prices, loading, error }: PriceBlocksCardProps) {
  const [expanded, setExpanded] = useState(false); // Start collapsed

  if (loading) {
    return (
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-brand-orange" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Electricity Prices</h3>
        </div>
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-orange"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-brand-orange" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Electricity Prices</h3>
        </div>
        <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!prices) {
    return (
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-brand-orange" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Electricity Prices</h3>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">No price data available</p>
      </div>
    );
  }

  // Build all 30-minute blocks for display
  // Historical + Current window + Forecasts
  const historicalBlocks = prices.historical_30min_blocks?.slice(-6) || []; // Last 6 settled blocks

  const currentBlock = {
    startTime: prices.current_30min.start_time,
    endTime: prices.current_30min.end_time,
    priceKwh: prices.current_30min.estimate_kwh,
    isCurrent: true,
    isForecast: false,
    periods: prices.periods,
  };

  const forecastBlocks = prices.forecast_30min_blocks.slice(0, 6); // Show 6 future blocks (3 hours)

  return (
    <div className="card p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-brand-orange" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            NEM Wholesale Prices
          </h3>
          <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
            {prices.region}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-green-500" />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {prices.generation.renewable_percentage}% renewable
          </span>
        </div>
      </div>

      {/* Current Price Summary */}
      <div className="grid grid-cols-3 gap-4 mb-5 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-800 rounded-lg">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Current 5min</p>
          <p className={`text-lg font-bold ${getPriceTextColor(prices.current.price_kwh)}`}>
            {formatPrice(prices.current.price_kwh)}
            <span className="text-xs font-normal text-gray-400">/kWh</span>
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">30min Average</p>
          <p className={`text-lg font-bold ${getPriceTextColor(prices.current_30min.average_kwh)}`}>
            {formatPrice(prices.current_30min.average_kwh)}
            <span className="text-xs font-normal text-gray-400">/kWh</span>
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400">30min Estimate</p>
          <p className={`text-lg font-bold ${getPriceTextColor(prices.current_30min.estimate_kwh)}`}>
            {formatPrice(prices.current_30min.estimate_kwh)}
            <span className="text-xs font-normal text-gray-400">/kWh</span>
          </p>
        </div>
      </div>

      {/* 30-minute blocks row */}
      <div className="overflow-x-auto py-2">
        <div className="flex gap-2 items-end pb-2">
          {/* Historical blocks section */}
          {historicalBlocks.length > 0 && (
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-1 mb-1.5">
                <History className="w-3 h-3 text-gray-400" />
                <span className="text-[10px] text-gray-400 font-medium">Past</span>
              </div>
              <div className="flex gap-1.5">
                {historicalBlocks.map((block, idx) => (
                  <HistoricalBlock key={idx} block={block} />
                ))}
              </div>
            </div>
          )}

          {/* Divider */}
          {historicalBlocks.length > 0 && (
            <div className="flex flex-col items-center justify-end h-full pb-4">
              <div className="w-px h-10 bg-gradient-to-b from-gray-200 to-gray-400 dark:from-gray-700 dark:to-gray-500"></div>
            </div>
          )}

          {/* Current 30-min block */}
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1 mb-1.5">
              <span className="text-[10px] text-brand-orange font-semibold">Current</span>
            </div>
            <ThirtyMinBlock
              startTime={currentBlock.startTime}
              endTime={currentBlock.endTime}
              priceKwh={currentBlock.priceKwh}
              isCurrent={true}
              isForecast={false}
              periods={currentBlock.periods}
              expanded={expanded}
              onToggle={() => setExpanded(!expanded)}
            />
          </div>

          {/* Divider */}
          {forecastBlocks.length > 0 && (
            <div className="flex flex-col items-center justify-end h-full pb-4">
              <div className="w-px h-10 bg-gradient-to-b from-gray-400 to-gray-200 dark:from-gray-500 dark:to-gray-700"></div>
            </div>
          )}

          {/* Forecast blocks section */}
          {forecastBlocks.length > 0 && (
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-1 mb-1.5">
                <TrendingUp className="w-3 h-3 text-gray-400" />
                <span className="text-[10px] text-gray-400 font-medium">Forecast</span>
              </div>
              <div className="flex gap-1.5">
                {forecastBlocks.map((block, idx) => (
                  <ThirtyMinBlock
                    key={idx}
                    startTime={block.start_time}
                    endTime={block.end_time}
                    priceKwh={block.price_kwh}
                    isCurrent={false}
                    isForecast={true}
                    expanded={false}
                    onToggle={() => {}}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-3 sm:gap-5 mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/50 text-[11px] text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-green-400"></div>
          <span>&lt;20c</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-yellow-400"></div>
          <span>20-30c</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-orange-400"></div>
          <span>30-40c</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-sm bg-red-400"></div>
          <span>&gt;40c</span>
        </div>
      </div>
    </div>
  );
}

export default PriceBlocksCard;
